// ============================================================================
// Authenticated user — profile + password endpoints.
//
// These power the in-app Settings page (web/src/features/settings) which
// every signed-in role can reach via the top-bar identity link.
// Distinct from /api/auth/password-reset/* which is the unauth'd
// "I forgot my password" flow.
// ============================================================================

import { api } from './client.js'

/**
 * PATCH the signed-in user's profile. Returns the same payload shape
 * as GET /api/auth/me/ so the FE can replace its cached `user` object
 * without a second round-trip.
 *
 * Editable keys (depend on role):
 *   - All users:    first_name, last_name, email
 *   - Clinicians:   specialty, qualifications, phone_number,
 *                   license_number, place_of_work, consultation_fee,
 *                   available_times, bio, years_of_experience,
 *                   profile_photo (file), signature (file)
 *
 * Username is intentionally NOT editable — changing it would orphan
 * audit trails. The backend ignores any `username` key in the body.
 *
 * If `patch` contains a File (e.g. for profile_photo / signature) it
 * is sent as multipart; otherwise it goes as JSON.
 */
export async function updateProfile(patch) {
  const hasFile = Object.values(patch || {}).some(
    (v) => v instanceof File || v instanceof Blob,
  )
  if (hasFile) {
    const fd = new FormData()
    for (const [k, v] of Object.entries(patch)) {
      if (v == null) {
        fd.append(k, '')          // explicit clear
      } else if (v instanceof File || v instanceof Blob) {
        fd.append(k, v)
      } else {
        fd.append(k, String(v))
      }
    }
    const { data } = await api.patch('/api/auth/me/', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  }
  const { data } = await api.patch('/api/auth/me/', patch)
  return data
}

/**
 * POST the signed-in user's password change. Requires the current
 * password (proof-of-identity even on a hijacked session) plus a new
 * password that satisfies Django's AUTH_PASSWORD_VALIDATORS.
 *
 * On success the API returns { detail: "Password updated." }. The
 * existing JWT pair stays valid — we don't force re-login because
 * doing so on every password change is hostile UX. If you want to
 * force re-login, sign out client-side after this call resolves.
 */
export async function changePassword({ currentPassword, newPassword }) {
  const { data } = await api.post('/api/auth/password/change/', {
    current_password: currentPassword,
    new_password: newPassword,
  })
  return data
}
